import axios from 'axios'
import { resolvePoPayloadForApi } from './portalPoAttachments.js'
import html2pdf from 'html2pdf.js'
import {
  conversionIncludesAllInProgressSeats,
  isLegacySeatLicenseType,
  isLicenseConversionDowngrade,
} from './portalOrganization.js'
import {
  buildBillToPayload,
  buildBillToPayloadForSubmit,
  formatBillToAddress,
  REFREPS_BILLING_FROM,
  refRepsBillingFromAddressLines,
} from './portalBillingForm.js'
import {
  formatDiscountAmountDisplay,
  formatDiscountLabel,
  formatTaxDisplay,
  formatTotalDisplay,
  resolveBillingDocumentDiscount,
  resolvePortalTotals,
} from './portalTaxDisplay.js'
import { buildInProgressSeatUpgradeSelection } from './portalInProgressSeatUpgrade.js'
import {
  descriptionForConversion,
  descriptionForNfhsAddon,
  descriptionForSeatPurchase,
  descriptionForSupplementalPacksPurchase,
  quickbooksProductForConversion,
  quickbooksProductForInProgressSeatUpgrade,
  quickbooksProductForNfhsAddon,
  quickbooksProductForSeatPurchase,
  quickbooksProductForSupplementalPacksPurchase,
} from './portalQuickbooksProducts.js'
import {
  buildStatePartnerDiscountNote,
  mergeBillingNotes,
} from './portalStatePartnerPricing.js'
import { instructorPortalOrderSourceFields } from './portalOrderSource.js'
import { unitPriceForKind } from '@/utils/nfhsPublicationAddon.js'
export { billToFromDocument } from './portalBillingForm.js'

import {
  endOfCalendarDay,
  formatBillingCalendarDate,
  isQuoteExpired,
  parseBillingCalendarDate,
} from './portalBillingDocumentDates.js'

const NET30_DAYS = 30
export { NET30_DAYS }

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Marks an `in_progress_seat_upgrade` line as a conversion companion:
 * same QB product for display, but learners are fulfilled only via
 * `cartSnapshot.conversionSeatSelection` / applyConversionFulfillment.
 */
export const CONVERSION_IN_PROGRESS_COMPANION_META = {
  conversionCompanion: true,
}

/**
 * Description for an in-progress seat upgrade line item,
 * e.g. "In-progress seat upgrade (Course Basic → Course Premium)".
 * @param {Record<string, unknown>} s
 * @param {{ includedSeats?: Array<{ fromLicense?: string }>, orgSeatLicenseType?: string }} selection
 */
export function inProgressSeatUpgradeDescription(s, selection) {
  const fmt =
    typeof s.formatBillingType === 'function' ? s.formatBillingType : (v) => v
  const toRaw = selection?.orgSeatLicenseType ?? s.seatLicenseType
  const fromRaw = selection?.includedSeats?.[0]?.fromLicense ?? 'Basic'
  return `In-progress seat upgrade (${fmt(fromRaw)} → ${fmt(toRaw)})`
}

/**
 * Description for in-progress seats shown as a companion line on an org conversion.
 * @param {Record<string, unknown>} s
 */
export function conversionInProgressCompanionDescription(s) {
  const fmt =
    typeof s.formatBillingType === 'function' ? s.formatBillingType : (v) => v
  const fromRaw = s.conversionCost?.from ?? 'Basic'
  const toRaw = s.conversionCost?.to ?? s.seatLicenseType
  return `In-progress seat upgrade (${fmt(fromRaw)} → ${fmt(toRaw)})`
}

/** @param {Record<string, unknown> | null | undefined} row */
export function isConversionInProgressCompanionLine(row) {
  return (
    row?.key === 'in_progress_seat_upgrade' &&
    row?.metadata?.conversionCompanion === true
  )
}

/**
 * Build line items matching the document editor table (cart snapshot at send time).
 * Partner carts use list (pre-discount) unit prices; discountAmount / discountPercent
 * on the payload carry the savings (seats + course premium upgrades).
 *
 * Org conversions with included in-progress seats emit two lines:
 * - `conversion` — unused pool only (`remainingSeatsToConvert`)
 * - `in_progress_seat_upgrade` with `metadata.conversionCompanion: true` —
 *   selected in-progress seats (same conversion unit price; QB product =
 *   RefReps In-Progress Seat Upgrade). Fulfillment for those seats stays on
 *   conversionSeatSelection — not the standalone in-progress upgrade path.
 *
 * @param {Record<string, unknown>} s
 */
export function buildEstimateQuoteLineItems(s) {
  const items = []

  if (s.conversionCost) {
    const listFee =
      s.listConversionFeePerSeat != null
        ? Number(s.listConversionFeePerSeat)
        : Number(s.conversionCost.feePerSeat) || 0
    const conversionSeatSelection = buildConversionSeatSelection(s)
    const remainingQty = Math.max(
      0,
      Number(conversionSeatSelection?.remainingSeatsToConvert) || 0
    )
    const includedInProgressQty = Math.max(
      0,
      Number(conversionSeatSelection?.includedInProgressCount) || 0
    )
    // Unused seats plus included in-progress seats. This is the value stored as
    // cartSnapshot.conversionSeatsAdjusted, which the server checks against.
    const seatsOnConversion = conversionSeatSelection
      ? remainingQty + includedInProgressQty
      : Math.max(0, Number(s.conversionSeatsAdjusted) || 0)
    // Split in-progress seats onto a companion line. Otherwise one line covers
    // the whole conversion and its quantity must equal seatsOnConversion.
    const conversionQty =
      includedInProgressQty > 0 ? remainingQty : seatsOnConversion
    const conversionAmount = conversionQty * listFee
    const addingNewSeats =
      Number(s.cartSeats) > 0 || Number(s.cartSupplementalPacks) > 0

    items.push({
      key: 'conversion',
      item: quickbooksProductForConversion(
        s.conversionCost.from,
        s.conversionCost.to,
        { addingNewSeats }
      ),
      description: descriptionForConversion(
        s.conversionCost.from,
        s.conversionCost.to
      ),
      quantity: conversionQty,
      unitPrice: listFee,
      amount: conversionAmount,
      metadata: {
        fromLicense: s.conversionCost.from,
        toLicense: s.conversionCost.to,
        feePerSeat: listFee,
        conversionSeatSelection,
      },
    })

    if (includedInProgressQty > 0) {
      items.push({
        key: 'in_progress_seat_upgrade',
        item: quickbooksProductForInProgressSeatUpgrade(),
        description: conversionInProgressCompanionDescription(s),
        quantity: includedInProgressQty,
        unitPrice: listFee,
        amount: includedInProgressQty * listFee,
        metadata: {
          ...CONVERSION_IN_PROGRESS_COMPANION_META,
          fromLicense: s.conversionCost.from,
          toLicense: s.conversionCost.to,
          feePerSeat: listFee,
          includedInProgressCount: includedInProgressQty,
          // Audit/display only — do not use for applyInProgressSeatUpgradeFulfillment.
          includedInProgress: conversionSeatSelection?.includedInProgress ?? [],
        },
      })
    }
  }

  if (
    s.inProgressSeatUpgradePending &&
    s.inProgressUpgradeConfirmed &&
    Number(s.inProgressUpgradeSubtotal) > 0
  ) {
    const selection = buildInProgressSeatUpgradeSelection(s)
    const listFee =
      s.listInProgressUpgradeFeePerSeat != null
        ? Number(s.listInProgressUpgradeFeePerSeat)
        : Number(selection.feePerSeat) || 0
    const listAmount =
      s.listInProgressUpgradeSubtotal != null
        ? Number(s.listInProgressUpgradeSubtotal)
        : selection.includedCount * listFee
    items.push({
      key: 'in_progress_seat_upgrade',
      item: quickbooksProductForInProgressSeatUpgrade(),
      description: inProgressSeatUpgradeDescription(s, selection),
      quantity: selection.includedCount,
      unitPrice: listFee,
      amount: listAmount,
      metadata: {
        orgSeatLicenseType: s.seatLicenseType,
        feePerSeat: listFee,
        inProgressSeatUpgradeSelection: {
          ...selection,
          feePerSeat: listFee,
        },
      },
    })
  }

  // New carts cannot sell Legacy seats. Stored unpaid orders keep their own line items
  // and still grant Legacy seats when paid — this builder is only for new documents.
  if (
    (Number(s.cartSeats) > 0 || Number(s.cartSupplementalPacks) > 0) &&
    isLegacySeatLicenseType(s.seatLicenseType)
  ) {
    throw new Error(
      'Legacy seats are no longer sold. Convert to Basic before adding seats.'
    )
  }

  if (s.cartSeats > 0) {
    const unit =
      s.listSeatCost != null
        ? Number(s.listSeatCost)
        : Number(s.seatCost) || 0
    const amount =
      s.listSeatSubtotal != null
        ? Number(s.listSeatSubtotal)
        : Number(s.cartSeats) * unit
    items.push({
      key: 'seats',
      item: quickbooksProductForSeatPurchase(s.seatLicenseType),
      description: descriptionForSeatPurchase(s.seatLicenseType),
      quantity: s.cartSeats,
      unitPrice: unit,
      amount,
      metadata: { licenseType: s.seatLicenseType },
    })
  }

  if (s.cartSupplementalPacks > 0) {
    items.push({
      key: 'supplemental_packs',
      item: quickbooksProductForSupplementalPacksPurchase(s.seatLicenseType),
      description: descriptionForSupplementalPacksPurchase(s.seatLicenseType),
      quantity: s.cartSupplementalPacks,
      unitPrice: 250,
      amount: s.supplementalSubtotal,
    })
  }

  if (s.nfhsLineInCart || s.nfhsStatus === 'pending' || (s.nfhsAddonTier && Number(s.nfhsSeatCount) > 0) || (Array.isArray(s.nfhsCartLines) && s.nfhsCartLines.length) || (Array.isArray(s.nfhsAddonKinds) && s.nfhsAddonKinds.length)) {
    let lines = Array.isArray(s.nfhsCartLines) && s.nfhsCartLines.length
      ? s.nfhsCartLines
      : []

    // Prefer explicit per-kind cart lines; otherwise expand kinds with per-kind pricing.
    if (!lines.length && Array.isArray(s.nfhsAddonKinds) && s.nfhsAddonKinds.length) {
      const remainingSeats = Math.max(0, Number(s.seatsPurchased) || 0)
      const newSeats = Math.max(0, Number(s.cartSeats) || 0)
      const pending = new Set(
        Array.isArray(s.nfhsPendingKinds) ? s.nfhsPendingKinds : (s.nfhsStatus === 'pending' ? s.nfhsAddonKinds : [])
      )
      lines = s.nfhsAddonKinds.map((kind) => {
        const isFirstPurchase = pending.has(kind)
        const includedInProgress = Number(
          s.nfhsAddonSeatSelectionByKind?.[kind]?.includedInProgress?.length
        ) || 0
        const seatCount = (isFirstPurchase ? remainingSeats : 0) + newSeats + includedInProgress
        // Add-ons are flat catalog pricing (no State Partner discount).
        const unitPrice = unitPriceForKind(kind)
        return {
          kind,
          seatCount,
          partnerUnitPrice: unitPrice,
          listUnitPrice: unitPrice,
          unitPrice,
          listSubtotal: Number((seatCount * unitPrice).toFixed(2)),
          subtotal: Number((seatCount * unitPrice).toFixed(2)),
          isFirstPurchase,
        }
      })
    } else if (!lines.length && s.nfhsAddonTier) {
      lines = [{
        kind: s.nfhsAddonTier,
        seatCount: Number(s.nfhsSeatCount) || 0,
        unitPrice: Number(s.nfhsUnitPrice) || 0,
        subtotal: Number(s.nfhsSubtotal) || 0,
        isFirstPurchase: s.nfhsStatus === 'pending',
      }]
    }

    for (const line of lines) {
      if (!(Number(line.seatCount) > 0)) continue
      // Flat catalog rate on the invoice line (no list markup / partner discount).
      const unit =
        line.unitPrice != null
          ? Number(line.unitPrice)
          : line.listUnitPrice != null
            ? Number(line.listUnitPrice)
            : unitPriceForKind(line.kind)
      const amount =
        line.subtotal != null
          ? Number(line.subtotal)
          : Number((Number(line.seatCount) * unit).toFixed(2))
      items.push({
        key: 'nfhs',
        item: quickbooksProductForNfhsAddon(line.kind),
        description: descriptionForNfhsAddon(line.kind),
        quantity: line.seatCount,
        unitPrice: unit,
        amount,
        metadata: {
          nfhsAddonKind: line.kind,
          nfhsAddonKinds: [line.kind],
          nfhsAddonTier: line.kind,
          partnerUnitPrice: line.partnerUnitPrice ?? unit,
          listUnitPrice: unit,
          payableUnitPrice: unit,
          nfhsAddonSeatSelection:
            s.nfhsAddonSeatSelectionByKind?.[line.kind]
            || (lines.length === 1 ? buildNfhsAddonSeatSelection(s) : null),
        },
      })
    }
  }

  return items
}

/**
 * @param {Record<string, unknown>} s
 */
function billingDiscountFields(s) {
  const amount = Number(s.discountAmount) || 0
  const percent = s.discountPercent != null ? Number(s.discountPercent) : null
  if (amount <= 0) {
    return { discountAmount: 0 }
  }
  return {
    discountAmount: amount,
    ...(percent != null && Number.isFinite(percent) && percent > 0
      ? { discountPercent: percent }
      : {}),
  }
}

/**
 * @param {Record<string, unknown>} s
 */
function billingDocumentSubtotal(s) {
  if (s.listSubtotal != null && (Number(s.discountAmount) || 0) > 0) {
    return Number(s.listSubtotal) || 0
  }
  return Number(s.subtotal) || 0
}

/**
 * Create-only: append State Partner discount explanation to user notes.
 * @param {Record<string, unknown>} s
 */
function billingDocumentNotes(s) {
  const amount = Number(s.discountAmount) || 0
  if (!(amount > 0)) return String(s.docFields?.notes ?? '')
  const partnerNote =
    s.statePartnerDiscountNote ||
    buildStatePartnerDiscountNote(s.discountPercent)
  return mergeBillingNotes(s.docFields?.notes, partnerNote)
}

/**
 * Request body for POST /generate-estimate-or-quote.
 * @param {Record<string, unknown>} s
 */
export function buildEstimateQuotePayload(s) {
  const issuedAt = new Date()
  const validUntil = new Date(issuedAt)
  validUntil.setDate(validUntil.getDate() + NET30_DAYS)

  return {
    companyId: s.companyId,
    documentType: 'quote',
    documentNumber: s.docNumber,
    status: 'sent',
    issuedAt: issuedAt.toISOString(),
    validUntil: validUntil.toISOString(),
    billTo: buildBillToPayloadForSubmit(s.docFields, s),
    notes: billingDocumentNotes(s),
    lineItems: buildEstimateQuoteLineItems(s),
    subtotal: billingDocumentSubtotal(s),
    ...billingDiscountFields(s),
    isTaxExempt: s.isTaxExempt,
    currency: 'USD',
    seatLicenseType: s.seatLicenseType,
    createdBy: s.createdBy,
    cartSnapshot: buildCartSnapshot(s),
    ...instructorPortalOrderSourceFields(),
  }
}

/**
 * @param {ReturnType<typeof buildEstimateQuotePayload> | Record<string, unknown>} payloadOrCart
 */
export async function postGenerateEstimateOrQuote(payloadOrCart) {
  const url = `${import.meta.env.VITE_BASE_LINK}/generate-estimate-or-quote`
  const payload =
    payloadOrCart?.companyId != null && payloadOrCart?.documentType === 'quote'
      ? payloadOrCart
      : buildEstimateQuotePayload(payloadOrCart)
  const { data } = await axios.post(
    url,
    { ...payload, ...instructorPortalOrderSourceFields() },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data
}

/**
 * One in-progress seat row for billing / fulfillment (stable at invoice create time).
 * @param {Record<string, unknown>} row
 */
export function serializeConversionSeatRow(row) {
  return {
    id: String(row.id ?? ''),
    studentId: row.studentId ?? null,
    courseId: row.courseId != null ? String(row.courseId) : null,
    name: String(row.name ?? ''),
    course: String(row.course ?? ''),
    contentType: row.contentType === 'pov' ? 'pov' : 'course',
  }
}

/**
 * Included vs excluded in-progress seats + unused pool count for conversion fulfillment.
 * @param {Record<string, unknown>} s
 */
function serializeNfhsAddonSeatRow(row) {
  return {
    id: row?.id != null ? String(row.id) : undefined,
    studentId: row?.studentId,
    courseId: row?.courseId ?? null,
    contentType: row?.contentType || 'course',
    name: row?.name || '',
    course: row?.course || '',
    sport: row?.sport || '',
  }
}

export function buildNfhsAddonSeatSelection(s) {
  if (s.nfhsAddonSeatSelectionByKind && typeof s.nfhsAddonSeatSelectionByKind === 'object') {
    const kinds = Object.keys(s.nfhsAddonSeatSelectionByKind)
    if (kinds.length === 1) {
      const row = s.nfhsAddonSeatSelectionByKind[kinds[0]] || {}
      return {
        includedInProgress: Array.isArray(row.includedInProgress) ? row.includedInProgress : [],
        excludedInProgress: Array.isArray(row.excludedInProgress) ? row.excludedInProgress : [],
        includedInProgressCount: Array.isArray(row.includedInProgress) ? row.includedInProgress.length : 0,
        excludedInProgressCount: Array.isArray(row.excludedInProgress) ? row.excludedInProgress.length : 0,
        confirmed: Boolean(row.confirmed),
        kind: kinds[0],
      }
    }
  }
  const kinds = Array.isArray(s.nfhsAddonKinds)
    ? s.nfhsAddonKinds
    : s.nfhsAddonTier
      ? [s.nfhsAddonTier]
      : []
  if (!kinds.length && !s.nfhsIncludeConfirmed) return null
  const excluded = Array.isArray(s.excludedFromAddOn) ? s.excludedFromAddOn : []
  const excludedIds = new Set(excluded.map((row) => String(row.id)))
  const eligible = Array.isArray(s.nfhsAddonEligibleLearners) ? s.nfhsAddonEligibleLearners : []
  const includedInProgress = s.nfhsIncludeConfirmed
    ? eligible.filter((row) => !excludedIds.has(String(row.id))).map(serializeNfhsAddonSeatRow)
    : []
  return {
    includedInProgress,
    excludedInProgress: excluded.map((row) => ({
      ...serializeNfhsAddonSeatRow(row),
      excludedAt: row.excludedAt ?? null,
    })),
    includedInProgressCount: includedInProgress.length,
    excludedInProgressCount: excluded.length,
    confirmed: Boolean(s.nfhsIncludeConfirmed),
    nfhsAddonKinds: kinds,
  }
}

export function buildConversionSeatSelection(s) {
  if (!s.conversionCost) return null

  const isDowngrade = conversionIncludesAllInProgressSeats(
    s.conversionCost.from,
    s.conversionCost.to
  )
  const excluded = isDowngrade
    ? []
    : Array.isArray(s.excludedFromConversion)
      ? s.excludedFromConversion
      : []
  const excludedIds = new Set(excluded.map((e) => String(e.id)))
  const active = Array.isArray(s.activeLearners) ? s.activeLearners : []

  const includedInProgress = active
    .filter((row) => !excludedIds.has(String(row.id)))
    .map(serializeConversionSeatRow)

  const excludedInProgress = excluded.map((e) => ({
    ...serializeConversionSeatRow(e),
    excludedAt: e.excludedAt ?? null,
  }))

  const remainingSeatsToConvert = Math.max(0, Number(s.seatsPurchased) || 0)

  return {
    includedInProgress,
    excludedInProgress,
    remainingSeatsToConvert,
    includedInProgressCount: includedInProgress.length,
    excludedInProgressCount: excludedInProgress.length,
  }
}

/**
 * Shared cart snapshot for quote and invoice payloads.
 * @param {Record<string, unknown>} s
 */
export function buildCartSnapshot(s) {
  const conversionSeatSelection = buildConversionSeatSelection(s)
  const allInProgressIncluded =
    s.conversionCost &&
    conversionIncludesAllInProgressSeats(s.conversionCost.from, s.conversionCost.to)

  const listConversionFeePerSeat =
    s.listConversionFeePerSeat != null
      ? Number(s.listConversionFeePerSeat)
      : Number(s.conversionCost?.feePerSeat) || 0
  const listInProgressUpgradeFeePerSeat =
    s.listInProgressUpgradeFeePerSeat != null
      ? Number(s.listInProgressUpgradeFeePerSeat)
      : Number(s.inProgressUpgradeFeePerSeat) || 0
  const listInProgressUpgradeSubtotal =
    s.listInProgressUpgradeSubtotal != null
      ? Number(s.listInProgressUpgradeSubtotal)
      : Number(s.inProgressUpgradeSubtotal) || 0

  // Snapshot fees match line-item list unit prices; partner savings live in discount fields.
  const conversionCost = s.conversionCost
    ? {
        ...s.conversionCost,
        feePerSeat: listConversionFeePerSeat,
      }
    : s.conversionCost

  const inProgressSeatUpgradeSelection = s.inProgressSeatUpgradePending
    ? {
        ...buildInProgressSeatUpgradeSelection(s),
        feePerSeat: listInProgressUpgradeFeePerSeat,
      }
    : null

  const selectionRemaining = Math.max(
    0,
    Number(conversionSeatSelection?.remainingSeatsToConvert) || 0
  )
  const selectionIncluded = Math.max(
    0,
    Number(conversionSeatSelection?.includedInProgressCount) || 0
  )
  // The server requires this to equal unused seats + included in-progress seats
  // (or the single conversion-line quantity when nobody is in progress).
  const conversionSeatsAdjusted = conversionSeatSelection
    ? selectionRemaining + selectionIncluded
    : s.conversionSeatsAdjusted

  return {
    cartSeats: s.cartSeats,
    cartSupplementalPacks: s.cartSupplementalPacks,
    conversionCost,
    conversionSeatsAdjusted,
    conversionSeatSelection,
    excludedFromConversion: allInProgressIncluded ? [] : s.excludedFromConversion ?? [],
    inProgressSeatUpgradePending: Boolean(s.inProgressSeatUpgradePending),
    inProgressUpgradeConfirmed: Boolean(s.inProgressUpgradeConfirmed),
    inProgressUpgradeSubtotal: listInProgressUpgradeSubtotal,
    inProgressUpgradeFeePerSeat: listInProgressUpgradeFeePerSeat,
    listConversionFeePerSeat,
    listInProgressUpgradeFeePerSeat,
    listInProgressUpgradeSubtotal,
    inProgressSeatUpgradeSelection,
    excludedFromInProgressUpgrade: s.excludedFromInProgressUpgrade ?? [],
    nfhsStatus: s.nfhsStatus,
    nfhsAddonTier: s.nfhsAddonTier || null,
    nfhsAddonKinds: Array.isArray(s.nfhsAddonKinds) ? s.nfhsAddonKinds : (s.nfhsAddonTier ? [s.nfhsAddonTier] : []),
    nfhsPendingKinds: Array.isArray(s.nfhsPendingKinds) ? s.nfhsPendingKinds : [],
    nfhsOrgKinds: Array.isArray(s.nfhsOrgKinds) ? s.nfhsOrgKinds : [],
    nfhsCartLines: Array.isArray(s.nfhsCartLines) ? s.nfhsCartLines : [],
    nfhsAddonSeatSelectionByKind: s.nfhsAddonSeatSelectionByKind || null,
    nfhsAddonLabel: s.nfhsAddonLabel || null,
    nfhsUnitPrice: Number(s.nfhsUnitPrice) || 0,
    nfhsSeatCount: Number(s.nfhsSeatCount) || 0,
    nfhsLineInCart: Boolean(s.nfhsLineInCart),
    nfhsIncludeConfirmed: Boolean(s.nfhsIncludeConfirmed),
    includedNfhsInProgress: Number(s.includedNfhsInProgress) || 0,
    excludedFromAddOn: s.excludedFromAddOn ?? [],
    nfhsAddonSeatSelection: buildNfhsAddonSeatSelection(s),
    seatLicenseType: s.seatLicenseType,
    seatsPurchased: s.seatsPurchased,
    learnerSelectionConfirmed: allInProgressIncluded
      ? true
      : Boolean(s.learnerSelectionConfirmed),
    listSubtotal: s.listSubtotal ?? null,
    discountAmount: Number(s.discountAmount) || 0,
    discountPercent: s.discountPercent != null ? Number(s.discountPercent) : null,
    qualifiesForStatePartnerPricing: Boolean(s.qualifiesForStatePartnerPricing),
  }
}

/**
 * Request body for POST /generate-invoice (no documentType).
 * @param {Record<string, unknown>} s
 */
export function buildInvoicePayload(s) {
  const sourceQuoteId = s.sourceQuoteId
    ? String(s.sourceQuoteId)
    : ''
  const po = resolvePoPayloadForApi(s.docFields?.poNumber, s.poAttachments)
  return {
    companyId: s.companyId,
    sourceQuoteId,
    billTo: buildBillToPayloadForSubmit(s.docFields, s),
    notes: billingDocumentNotes(s),
    poNumber: po.poNumber,
    lineItems: buildEstimateQuoteLineItems(s),
    subtotal: billingDocumentSubtotal(s),
    ...billingDiscountFields(s),
    isTaxExempt: s.isTaxExempt,
    currency: 'USD',
    seatLicenseType: s.seatLicenseType,
    cartSnapshot: buildCartSnapshot(s),
    poAttachments: po.poAttachments,
    createdBy: s.createdBy,
    ...instructorPortalOrderSourceFields(),
  }
}

/**
 * QuickBooks hosted estimate URL from API document.
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function getQuickBooksEstimateUrl(doc) {
  if (!doc) return ''
  return (
    doc.quickbooksEstimateUrl ||
    doc.estimateUrl ||
    (doc.documentType === 'quote' ? doc.shareUrl : '') ||
    ''
  )
}

/**
 * QuickBooks hosted invoice URL from API document.
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function getQuickBooksInvoiceUrl(doc) {
  if (!doc) return ''
  return (
    doc.invoiceUrl ||
    doc.quickbooksInvoiceLink ||
    (doc.documentType === 'invoice' ? doc.shareUrl : '') ||
    ''
  )
}

/**
 * @param {unknown} err axios error
 */
export function parseBillingApiError(err) {
  const status = err?.response?.status
  const data = err?.response?.data
  const message =
    data?.message ||
    data?.error ||
    err?.message ||
    'Request failed'
  return {
    status,
    message: String(message),
    document: data?.document ?? null,
    documentId: data?.documentId ?? data?.document?.id ?? null,
    poReviewStatus: data?.poReviewStatus ?? data?.document?.poReviewStatus ?? null,
  }
}

/**
 * @param {ReturnType<typeof buildInvoicePayload>} payload
 */
export async function postGenerateInvoice(payload) {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/generate-invoice`,
      { ...payload, ...instructorPortalOrderSourceFields() },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return data
  } catch (err) {
    const parsed = parseBillingApiError(err)
    const e = new Error(parsed.message)
    e.status = parsed.status
    e.document = parsed.document
    e.documentId = parsed.documentId
    e.poReviewStatus = parsed.poReviewStatus
    throw e
  }
}

/** Stable id for POST /fulfill-license-conversion retries. */
export function createConversionRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `conv-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * $0 conversion fulfillment (e.g. Premium → Basic, Legacy → Basic).
 * Allows the conversion line plus an optional conversion-companion
 * `in_progress_seat_upgrade` line (same seats still fulfilled via conversionSeatSelection).
 * @param {Record<string, unknown>} s
 */
export function buildFulfillLicenseConversionPayload(s) {
  const lineItems = buildEstimateQuoteLineItems(s).filter(
    (row) =>
      row.key === 'conversion' || isConversionInProgressCompanionLine(row)
  )
  const conversionLines = lineItems.filter((row) => row.key === 'conversion')
  if (conversionLines.length !== 1) {
    throw new Error('A single conversion line item is required')
  }
  const companionLines = lineItems.filter(isConversionInProgressCompanionLine)
  if (companionLines.length > 1) {
    throw new Error('At most one conversion in-progress companion line is allowed')
  }
  const snapshot = buildCartSnapshot(s)
  if (!snapshot.conversionCost || !snapshot.conversionSeatSelection) {
    throw new Error('Conversion cart snapshot is incomplete')
  }

  return {
    companyId: s.companyId,
    conversionRequestId: s.conversionRequestId || createConversionRequestId(),
    seatLicenseType: s.seatLicenseType,
    lineItems,
    cartSnapshot: snapshot,
    subtotal: 0,
    taxAmount: 0,
    isTaxExempt: Boolean(s.isTaxExempt),
    total: 0,
    currency: 'USD',
    billTo: buildBillToPayloadForSubmit(s.docFields ?? {}, s),
    notes: s.docFields?.notes ?? '',
    ...resolvePoPayloadForApi(s.docFields?.poNumber, s.poAttachments),
    createdBy: s.createdBy,
    ...instructorPortalOrderSourceFields(),
  }
}

/**
 * @param {ReturnType<typeof buildFulfillLicenseConversionPayload>} payload
 */
export async function postFulfillLicenseConversion(payload) {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/fulfill-license-conversion`,
      { ...payload, ...instructorPortalOrderSourceFields() },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return data
  } catch (err) {
    const parsed = parseBillingApiError(err)
    const e = new Error(parsed.message)
    e.status = parsed.status
    e.document = parsed.document
    e.documentId = parsed.documentId
    throw e
  }
}

/** @param {unknown} lineItems */
export function sumLineItemAmounts(lineItems) {
  if (!Array.isArray(lineItems)) return 0
  return lineItems.reduce((sum, row) => sum + (Number(row?.amount) || 0), 0)
}

/** Line item keys whose quantity can be edited on saved estimates/quotes. */
export const EDITABLE_ESTIMATE_LINE_ITEM_KEYS = new Set([
  'seats',
  'supplemental_packs',
  'nfhs',
])

const EDITABLE_ESTIMATE_LINE_ITEM_MIN_QTY = {
  seats: 1,
  supplemental_packs: 0,
  nfhs: 1,
}

/** @param {number} n */
export function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

/** @param {unknown} lineItems */
export function recalculateEstimateLineItems(lineItems) {
  if (!Array.isArray(lineItems)) return []
  return lineItems.map((row) => {
    const quantity = Number(row?.quantity) || 0
    const unitPrice = Number(row?.unitPrice) || 0
    return {
      ...row,
      quantity,
      unitPrice,
      amount: roundMoney(quantity * unitPrice),
    }
  })
}

/** @param {string} key @param {unknown} rawQty */
export function clampEditableEstimateQuantity(key, rawQty) {
  const min = EDITABLE_ESTIMATE_LINE_ITEM_MIN_QTY[key] ?? 0
  const n = Math.max(0, Math.floor(Number(rawQty) || 0))
  return Math.max(min, n)
}

/** Backend soft-delete / void — hidden from purchase history lists. */
export function isBillingDocumentHiddenFromPurchaseHistory(doc) {
  const status = String(doc?.status || '').toLowerCase()
  return status === 'deleted' || status === 'void' || status === 'voided'
}

/** Quotes stay editable until converted to a QuickBooks invoice. */
export function isQuoteEditable(doc) {
  if (!doc || doc.documentType !== 'quote') return false
  const status = String(doc.status || '').toLowerCase()
  if (['deleted', 'void', 'voided'].includes(status)) return false
  if (isQuoteConverted(doc)) return false
  if (isQuoteExpired(doc)) return false
  return true
}

/** Uninvoiced quote that is still within its valid-until date. */
export function isQuoteInvoiceable(doc) {
  return isQuoteEditable(doc)
}

/** Unpaid QuickBooks invoices — billing info, PO, notes, and tax exempt only. */
export function isInvoiceEditable(doc) {
  if (!doc || doc.documentType !== 'invoice') return false
  const status = String(doc.status || '').toLowerCase()
  if (['deleted', 'void', 'voided', 'paid', 'po_received'].includes(status)) return false
  if (String(doc.fulfillmentStatus || '').toLowerCase() === 'fulfilled') return false
  return true
}

/** Saved quote or unpaid invoice rows editable for billing metadata (not line quantities). */
export function isBillingDocumentFieldsEditable(doc) {
  return isQuoteEditable(doc) || isInvoiceEditable(doc)
}

/** Purchase-history row label: uninvoiced quote editable for billing fields. */
export function isPurchaseHistoryQuoteFieldsEditable(entry, docForEntry) {
  if (docForEntry) return isQuoteEditable(docForEntry)
  if (!isPurchaseHistoryQuoteEntry(entry)) return false
  if (entry.quickbooksInvoiceId || entry.convertedToInvoiceId) return false
  const status = String(entry.status || '').toLowerCase()
  if (['deleted', 'void', 'voided', 'converted'].includes(status)) return false
  if (isQuoteExpired(entry.billingDocument ?? entry)) return false
  return true
}

/** Purchase-history row label: unpaid invoice editable for billing fields. */
export function isPurchaseHistoryInvoiceFieldsEditable(entry, docForEntry) {
  if (docForEntry) return isInvoiceEditable(docForEntry)
  if (!entry || (entry.documentType !== 'invoice' && entry.type !== 'Invoice')) return false
  return isPurchaseHistoryInvoiceUnpaid(entry)
}

/** @param {Record<string, unknown>} doc */
export function isQuoteConverted(doc) {
  if (!doc || doc.documentType !== 'quote') return false
  const status = String(doc.status || '').toLowerCase()
  return (
    status === 'converted' ||
    Boolean(doc.convertedToInvoiceId || doc.quickbooksInvoiceId)
  )
}

/**
 * Effective status for PDF / preview badges (expired + converted overrides).
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function resolveBillingDocumentDisplayStatus(doc) {
  if (!doc) return 'sent'
  if (doc.documentType === 'quote') {
    if (isQuoteConverted(doc)) return 'converted'
    if (isQuoteExpired(doc)) return 'expired'
  }
  const status = String(doc.status || '').trim().toLowerCase()
  return status || (doc.documentType === 'quote' ? 'sent' : 'open')
}

/**
 * Uninvoiced estimates should include QuickBooks follow-up copy on PDF exports.
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function shouldIncludeEstimateQuickBooksPdfNote(doc) {
  if (!doc || doc.documentType !== 'quote') return false
  return !isQuoteConverted(doc)
}

export const ESTIMATE_QUICKBOOKS_PDF_NOTE =
  'To approve, decline, or convert this estimate to an invoice, use the estimate link in the email you received from QuickBooks. This PDF is for viewing and record-keeping only.'

export { poAttachmentsFromDocument } from './portalPoAttachments.js'

/**
 * @param {Record<string, unknown>} doc
 */
export function documentNumberPlain(doc) {
  return String(doc?.documentNumber ?? '').replace(/^#/, '')
}

/**
 * @param {Record<string, unknown>} s
 */
export function buildEstimateQuoteUpdatePayload(s) {
  const subtotal =
    Number(s.subtotal) || sumLineItemAmounts(s.lineItems)
  return {
    companyId: s.companyId,
    documentId: s.documentId,
    documentNumber: s.documentNumber,
    billTo: buildBillToPayloadForSubmit(s.docFields, s),
    notes: s.docFields.notes ?? '',
    poNumber: '',
    lineItems: s.lineItems ?? [],
    subtotal,
    isTaxExempt: s.isTaxExempt,
    currency: 'USD',
    poAttachments: [],
    ...instructorPortalOrderSourceFields(),
  }
}

/** Invoice updates include optional PO number + document pair. */
export function buildInvoiceUpdatePayload(s) {
  const subtotal =
    Number(s.subtotal) || sumLineItemAmounts(s.lineItems)
  const po = resolvePoPayloadForApi(s.docFields?.poNumber, s.poAttachments)
  return {
    companyId: s.companyId,
    documentId: s.documentId,
    documentNumber: s.documentNumber,
    billTo: buildBillToPayloadForSubmit(s.docFields, s),
    notes: s.docFields.notes ?? '',
    poNumber: po.poNumber,
    lineItems: s.lineItems ?? [],
    subtotal,
    isTaxExempt: s.isTaxExempt,
    currency: 'USD',
    poAttachments: po.poAttachments,
    ...instructorPortalOrderSourceFields(),
  }
}

/**
 * @param {ReturnType<typeof buildEstimateQuoteUpdatePayload>} payload
 */
export async function postUpdateEstimateOrQuote(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/update-estimate-or-quote`,
    { ...payload, ...instructorPortalOrderSourceFields() },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data
}

/**
 * @param {ReturnType<typeof buildInvoiceUpdatePayload>} payload
 */
export async function postUpdateInvoice(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/update-invoice`,
    { ...payload, ...instructorPortalOrderSourceFields() },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data
}

/**
 * @param {{ companyId: string, documentId: string, documentNumber?: string }} payload
 */
export async function postDeleteEstimateOrQuote(payload) {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/delete-estimate-or-quote`,
      payload,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return data
  } catch (err) {
    const parsed = parseBillingApiError(err)
    const e = new Error(parsed.message)
    e.status = parsed.status
    throw e
  }
}

/**
 * @param {{ companyId: string, documentId: string, documentNumber?: string }} payload
 */
export async function postVoidInvoice(payload) {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/void-invoice`,
      payload,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return data
  } catch (err) {
    const parsed = parseBillingApiError(err)
    const e = new Error(parsed.message)
    e.status = parsed.status
    throw e
  }
}

/** @param {string | Date | undefined} iso */
export function formatBillingDocumentDate(iso) {
  return formatBillingCalendarDate(iso)
}

/** @param {string | undefined} status */
export function billingDocumentStatusMeta(status) {
  const key = String(status || 'sent').toLowerCase()
  const map = {
    draft: {
      label: 'Draft',
      class: 'bg-black/5 text-gray-600',
      emailBadgeStyle: 'background:rgba(0,0,0,0.06);color:#666',
    },
    sent: {
      label: 'Sent',
      class: 'bg-teal-500/10 text-teal-500',
      emailBadgeStyle: 'background:rgba(20,184,166,0.12);color:#14b8a6',
    },
    approved: {
      label: 'Approved',
      class: 'bg-teal-500/[0.18] text-teal-600',
      emailBadgeStyle: 'background:rgba(20,184,166,0.18);color:#0d9488',
    },
    declined: {
      label: 'Declined',
      class: 'bg-red-500/10 text-red-600',
      emailBadgeStyle: 'background:rgba(220,53,69,0.1);color:#dc3545',
    },
    expired: {
      label: 'Expired',
      class: 'bg-orange-500/10 text-orange-500',
      emailBadgeStyle: 'background:rgba(230,126,34,0.12);color:#e67e22',
    },
    converted: {
      label: 'Invoiced',
      class: 'bg-slate-900/5 text-slate-900',
      emailBadgeStyle: 'background:rgba(26,26,46,0.08);color:#1a1a2e',
    },
    open: {
      label: 'Open',
      class: 'bg-slate-900/5 text-slate-900',
      emailBadgeStyle: 'background:rgba(26,26,46,0.08);color:#1a1a2e',
    },
    po_received: {
      label: 'PO Received',
      class: 'bg-amber-500/10 text-amber-600',
      emailBadgeStyle: 'background:rgba(245,158,11,0.16);color:#b45309',
    },
    paid: {
      label: 'Paid',
      class: 'bg-teal-500/[0.18] text-teal-600',
      emailBadgeStyle: 'background:rgba(20,184,166,0.18);color:#0d9488',
    },
    void: {
      label: 'Voided',
      class: 'bg-red-500/10 text-red-600',
      emailBadgeStyle: 'background:rgba(220,53,69,0.1);color:#dc3545',
    },
    voided: {
      label: 'Voided',
      class: 'bg-red-500/10 text-red-600',
      emailBadgeStyle: 'background:rgba(220,53,69,0.1);color:#dc3545',
    },
    deleted: {
      label: 'Deleted',
      class: 'bg-gray-500/10 text-gray-600',
      emailBadgeStyle: 'background:rgba(0,0,0,0.06);color:#666',
    },
  }
  return map[key] || map.sent
}

/** QuickBooks estimate rows that can be removed from purchase history. */
export function isPurchaseHistoryQuoteDeletable(entry, docForEntry) {
  if (!entry || entry.isSeatBatchRow || entry.documentType === 'seat_batch') return false
  const isQuote =
    entry.documentType === 'quote' || entry.type === 'Estimate/Quote'
  if (!isQuote) return false
  const doc = docForEntry || entry.billingDocument
  if (doc && isQuoteConverted(doc)) return false
  if (doc && isQuoteExpired(doc)) return false
  if (!doc && isQuoteExpired(entry)) return false
  const status = String(entry.status || doc?.status || '').toLowerCase()
  if (['deleted', 'void', 'voided'].includes(status)) return false
  const qbId = doc?.quickbooksEstimateId ?? entry.quickbooksEstimateId
  const qbUrl = getQuickBooksEstimateUrl(doc || entry)
  return Boolean(qbId || qbUrl)
}

/** @param {Record<string, unknown>} entry */
export function isPurchaseHistoryInvoiceUnpaid(entry) {
  const status = String(entry?.status || '').toLowerCase()
  const docStatus = String(entry?.billingDocument?.status || '').toLowerCase()
  if (status === 'paid' || status === 'po_received') return false
  if (docStatus === 'po_received') return false
  if (entry?.documentType === 'payment' || entry?.type === 'Payment') return false
  const fulfillment = String(entry?.fulfillmentStatus || '').toLowerCase()
  if (fulfillment === 'fulfilled') return false
  return true
}

/**
 * Payment due date for a purchase-history invoice row.
 * Falls back to issued date + NET 30 when dueAt is missing.
 * @param {Record<string, unknown>} entry
 */
export function resolvePurchaseHistoryInvoiceDueDate(entry) {
  if (!entry) return null
  const isInvoice =
    entry.documentType === 'invoice' || entry.type === 'Invoice'
  if (!isInvoice) return null

  const doc = entry.billingDocument
  const raw = entry.dueAt ?? doc?.dueAt
  if (raw) {
    const parsed = parseBillingCalendarDate(raw)
    if (parsed) return parsed
  }

  const issued = parseBillingCalendarDate(doc?.issuedAt ?? entry.purchaseDate)
  if (!issued) return null
  const fallback = new Date(issued)
  fallback.setDate(fallback.getDate() + NET30_DAYS)
  return fallback
}

/**
 * Days until invoice payment due date (unpaid invoices only).
 * @param {Record<string, unknown>} entry
 * @param {Date} [now]
 */
export function computePurchaseHistoryInvoiceDueDaysLeft(entry, now = new Date()) {
  if (!entry) return null
  if (entry.isSeatBatchRow || entry.documentType === 'seat_batch') return null
  if (entry.documentType === 'payment' || entry.type === 'Payment') return null

  const status = String(
    entry.status ?? entry.billingDocument?.status ?? ''
  ).toLowerCase()
  if (status === 'voided' || status === 'void') return null
  if (!isPurchaseHistoryInvoiceUnpaid(entry)) return null

  const dueDate = resolvePurchaseHistoryInvoiceDueDate(entry)
  if (!dueDate) return null

  return Math.max(0, Math.ceil((endOfCalendarDay(dueDate) - now) / 86400000))
}

/**
 * Formatted payment due date for purchase-history invoice rows (includes NET 30 fallback).
 * @param {Record<string, unknown>} entry
 */
export function formatPurchaseHistoryInvoiceDueDateLabel(entry) {
  if (!entry) return null
  const isInvoice =
    entry.documentType === 'invoice' || entry.type === 'Invoice'
  if (!isInvoice) return null
  const dueDate = resolvePurchaseHistoryInvoiceDueDate(entry)
  if (!dueDate) return null
  return formatBillingDocumentDate(dueDate)
}

/** Unpaid QuickBooks invoice rows that can be voided from purchase history. */
export function isPurchaseHistoryInvoiceVoidable(entry) {
  if (!entry || entry.isSeatBatchRow || entry.documentType === 'seat_batch') return false
  const isInvoice = entry.documentType === 'invoice' || entry.type === 'Invoice'
  if (!isInvoice) return false
  const status = String(entry.status || '').toLowerCase()
  if (status === 'voided' || status === 'void' || status === 'deleted') return false
  if (!isPurchaseHistoryInvoiceUnpaid(entry)) return false
  const qbId =
    entry.quickbooksInvoiceId ?? entry.billingDocument?.quickbooksInvoiceId
  const qbUrl = getQuickBooksInvoiceUrl(entry.billingDocument || entry)
  return Boolean(qbId || qbUrl || entry.invoiceUrl || entry.shareUrl)
}

/** @param {Record<string, unknown> | null | undefined} entry */
export function isPurchaseHistoryQuoteEntry(entry) {
  return entry?.documentType === 'quote' || entry?.type === 'Estimate/Quote'
}

/** @param {Record<string, unknown> | null | undefined} entry */
export function isPurchaseHistoryInvoiceTabEntry(entry) {
  return !isPurchaseHistoryQuoteEntry(entry)
}

/** @param {string | undefined} documentType */
export function billingDocumentListType(documentType) {
  if (documentType === 'quote') return 'Estimate/Quote'
  if (documentType === 'invoice') return 'Invoice'
  if (documentType === 'payment') return 'Payment'
  return 'Estimate/Quote'
}

/** @param {unknown} lineItems */
function seatsFromLineItems(lineItems) {
  if (!Array.isArray(lineItems)) return undefined
  const seatRow = lineItems.find((i) => i?.key === 'seats')
  return seatRow ? Number(seatRow.quantity) || undefined : undefined
}

/** @param {unknown} lineItems */
function conversionFromLineItems(lineItems) {
  if (!Array.isArray(lineItems)) return undefined
  const row = lineItems.find((i) => i?.key === 'conversion')
  if (!row) return undefined
  const companion = lineItems.find(isConversionInProgressCompanionLine)
  const remaining = Number(row.quantity) || 0
  const included = companion ? Number(companion.quantity) || 0 : 0
  return remaining + included
}

/**
 * Map API billing document to portal purchase history list row.
 * @param {Record<string, unknown>} doc
 */
export function mapBillingDocumentToPurchaseHistoryEntry(doc) {
  const number = doc.documentNumber
    ? String(doc.documentNumber).startsWith('#')
      ? doc.documentNumber
      : `#${doc.documentNumber}`
    : ''
  const issued = doc.issuedAt ? new Date(doc.issuedAt) : new Date()
  const id = doc.id ?? doc._id ?? doc.documentNumber
  const statusMeta = billingDocumentStatusMeta(doc.status)
  const isInvoice = doc.documentType === 'invoice'
  const qbUrl = isInvoice ? getQuickBooksInvoiceUrl(doc) : ''
  const entry = {
    id,
    number,
    date: formatBillingDocumentDate(doc.issuedAt || issued),
    amount: Number(doc.total) || 0,
    type: billingDocumentListType(doc.documentType),
    purchaseDate: issued.toISOString().slice(0, 10),
    documentId: doc.id ?? doc._id,
    documentType: doc.documentType,
    status: doc.status,
    statusLabel: statusMeta.label,
    validUntil: doc.validUntil ?? null,
    validUntilFormatted: formatBillingDocumentDate(doc.validUntil),
    dueAt: doc.dueAt ?? null,
    dueAtFormatted: formatBillingDocumentDate(doc.dueAt),
    shareToken: doc.shareToken,
    shareUrl: isInvoice ? qbUrl : doc.shareUrl,
    invoiceUrl: qbUrl,
    quickbooksInvoiceId: doc.quickbooksInvoiceId,
    quickbooksEstimateId: doc.quickbooksEstimateId,
    sourceQuoteId: doc.sourceQuoteId,
    license: doc.seatLicenseType,
    seats: seatsFromLineItems(doc.lineItems),
    conversionSeats: conversionFromLineItems(doc.lineItems),
    hasConversionLine: Array.isArray(doc.lineItems) &&
      doc.lineItems.some((i) => i?.key === 'conversion'),
    fulfillmentStatus: doc.fulfillmentStatus ?? null,
    paidAt: doc.paidAt ?? null,
    fulfilledAt: doc.fulfilledAt ?? null,
    lineItems: Array.isArray(doc.lineItems) ? doc.lineItems : [],
  }
  const conversionLine = entry.lineItems.find((row) => row?.key === 'conversion')
  if (conversionLine) {
    entry.conversionFromLicense = conversionLine.metadata?.fromLicense ?? null
    entry.conversionToLicense = conversionLine.metadata?.toLicense ?? null
    entry.conversionDescription = conversionLine.item ?? null
  }
  entry.hasSupplementalLine = entry.lineItems.some((row) => row?.key === 'supplemental_packs')
  entry.hasNfhsLine = entry.lineItems.some((row) => row?.key === 'nfhs')
  entry.hasNfhsPublicationAssignLine = entry.lineItems.some(
    (row) => row?.key === 'nfhs_publication_assign'
  )
  if (entry.hasNfhsPublicationAssignLine) {
    const assignLine = entry.lineItems.find((row) => row?.key === 'nfhs_publication_assign')
    const snap = doc.cartSnapshot?.nfhsPublicationAssign
    const meta = assignLine?.metadata && typeof assignLine.metadata === 'object'
      ? assignLine.metadata
      : {}
    const emails = [
      ...new Set(
        [...(Array.isArray(meta.emails) ? meta.emails : []), ...(Array.isArray(snap?.emails) ? snap.emails : [])]
          .map((email) => String(email || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    ]
    entry.nfhsPublicationAssignQuantity =
      emails.length || Number(assignLine?.quantity) || Number(snap?.quantity) || 0
    entry.nfhsPublicationAssignEmails = emails
    entry.nfhsPublicationAssignBookTitle = String(
      meta.bookTitle || snap?.bookTitle || ''
    ).trim()
  }
  entry.hasQuickBooksPdf = Boolean(doc.hasQuickBooksPdf)
  if (doc.sourceQuote) {
    entry.fromQuote = doc.sourceQuote
  }
  entry.billingDocument = doc
  return entry
}

/**
 * Minimal document for preview when only the purchase-history row is available.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown>} [billToFallback]
 */
export function buildBillingDocumentFromHistoryEntry(entry, billToFallback = {}) {
  const num = String(entry.number || '').replace(/^#/, '')
  const purchaseDate = entry.purchaseDate || new Date().toISOString().slice(0, 10)
  const issued = new Date(purchaseDate)
  const validUntil = new Date(issued)
  validUntil.setDate(validUntil.getDate() + 30)
  const amount = Number(entry.amount) || 0
  const typeLabel = billingDocumentListType(entry.documentType || entry.type)
  const lineItems = Array.isArray(entry.lineItems) && entry.lineItems.length > 0
    ? entry.lineItems
    : amount > 0
      ? [
          {
            item: typeLabel,
            description: entry.number ? String(entry.number) : typeLabel,
            quantity: 1,
            unitPrice: amount,
            amount,
          },
        ]
      : []
  return {
    id: entry.documentId ?? entry.id,
    documentType:
      entry.documentType ||
      (entry.type === 'Invoice'
        ? 'invoice'
        : entry.type === 'Payment'
          ? 'payment'
          : 'quote'),
    documentNumber: num,
    status:
      entry.status ||
      (entry.documentType === 'invoice' || entry.type === 'Invoice'
        ? isPurchaseHistoryInvoiceUnpaid(entry)
          ? 'sent'
          : 'paid'
        : 'sent'),
    fulfillmentStatus:
      entry.fulfillmentStatus ?? entry.billingDocument?.fulfillmentStatus ?? null,
    convertedToInvoiceId:
      entry.convertedToInvoiceId ?? entry.billingDocument?.convertedToInvoiceId ?? null,
    issuedAt: issued.toISOString(),
    validUntil: validUntil.toISOString(),
    billTo: buildBillToPayload(billToFallback),
    lineItems,
    subtotal: Number(entry.subtotal) || amount,
    taxAmount: entry.taxAmount != null ? Number(entry.taxAmount) : null,
    discountAmount:
      entry.discountAmount != null
        ? Number(entry.discountAmount)
        : entry.billingDocument?.discountAmount != null
          ? Number(entry.billingDocument.discountAmount)
          : null,
    discountPercent:
      entry.discountPercent != null
        ? Number(entry.discountPercent)
        : entry.billingDocument?.discountPercent != null
          ? Number(entry.billingDocument.discountPercent)
          : null,
    isTaxExempt: Boolean(entry.isTaxExempt ?? entry.billingDocument?.isTaxExempt),
    total: entry.total != null ? Number(entry.total) : amount,
    taxRate: entry.taxRate ?? entry.billingDocument?.taxRate ?? null,
    quickbooksEstimateId:
      entry.quickbooksEstimateId ?? entry.billingDocument?.quickbooksEstimateId ?? null,
    quickbooksEstimateUrl:
      entry.quickbooksEstimateUrl ?? entry.billingDocument?.quickbooksEstimateUrl ?? null,
    quickbooksInvoiceId:
      entry.quickbooksInvoiceId ?? entry.billingDocument?.quickbooksInvoiceId ?? null,
    seatLicenseType: entry.license,
    notes: '',
  }
}

/**
 * Resolve billing document for download / preview from a purchase-history row.
 */
export function resolveBillingDocumentForDownload(docs, entry, billToFallback = {}) {
  if (!entry) return null
  let doc = findBillingDocumentForEntry(docs, entry)
  const isQuote =
    entry?.type === 'Estimate/Quote' || entry?.documentType === 'quote'
  if (!doc && (isQuote || entry?.documentType === 'payment' || entry?.type === 'Payment')) {
    doc = buildBillingDocumentFromHistoryEntry(entry, billToFallback)
  }
  if (!doc && (entry?.documentType === 'invoice' || entry?.type === 'Invoice')) {
    doc = buildBillingDocumentFromHistoryEntry(
      { ...entry, documentType: 'invoice' },
      billToFallback
    )
  }
  return doc
}

/**
 * HTML used for client-side PDF export.
 * @param {Record<string, unknown>} doc
 * @param {{ orgTaxExempt?: boolean }} [options]
 */
export function buildBillingDocumentPdfHtml(doc, options = {}) {
  const isQuote = doc.documentType === 'quote'
  const typeLabel = billingDocumentListType(doc.documentType)
  const displayStatus = resolveBillingDocumentDisplayStatus(doc)
  const statusMeta = billingDocumentStatusMeta(displayStatus)
  const lineItems = Array.isArray(doc.lineItems) ? doc.lineItems : []
  const num = String(doc.documentNumber || '').replace(/^#/, '')
  const billTo = doc.billTo || {}
  const subtotal = Number(doc.subtotal) || 0
  const totals = resolvePortalTotals(
    subtotal,
    Boolean(options.orgTaxExempt),
    doc
  )
  const taxResolved = !totals.taxPending && !totals.isTaxExempt
  const taxLabel = totals.isTaxExempt
    ? 'Tax exempt'
    : taxResolved && doc.taxRate != null
      ? `${Math.round(Number(doc.taxRate) * 100)}%`
      : 'Pending'
  const taxValue = formatTaxDisplay({
    isTaxExempt: totals.isTaxExempt,
    taxPending: totals.taxPending,
    taxAmount: totals.taxAmount,
  })
  const totalValue = formatTotalDisplay({
    isTaxExempt: totals.isTaxExempt,
    taxPending: totals.taxPending,
    subtotal: totals.subtotal,
    total: totals.total,
  })

  const rows =
    lineItems.length > 0
      ? lineItems
          .map(
            (row) => `
      <tr>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;font-weight:600;">${escapeHtml(row.item)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;color:#64748b;">${escapeHtml(row.description)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;">${escapeHtml(row.quantity)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;">$${Number(row.unitPrice || 0).toFixed(2)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;font-weight:700;">$${Number(row.amount || 0).toFixed(2)}</td>
      </tr>`
          )
          .join('')
      : `<tr><td colspan="5" style="padding:16px;text-align:center;color:#94a3b8;">No line items recorded</td></tr>`

  const dateRows = [
    `<div><span style="display:block;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;">Issued</span><span style="font-size:13px;font-weight:600;">${escapeHtml(formatBillingDocumentDate(doc.issuedAt))}</span></div>`,
  ]
  if (doc.validUntil) {
    dateRows.push(
      `<div><span style="display:block;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;">${isQuote ? 'Valid until' : 'Valid until'}</span><span style="font-size:13px;font-weight:600;">${escapeHtml(formatBillingDocumentDate(doc.validUntil))}</span></div>`
    )
  }
  if (doc.dueAt) {
    dateRows.push(
      `<div><span style="display:block;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;">Due</span><span style="font-size:13px;font-weight:600;">${escapeHtml(formatBillingDocumentDate(doc.dueAt))}</span></div>`
    )
  }

  const totalsRows = `
    <tr><td style="padding:6px 0;color:#64748b;">Subtotal</td><td style="padding:6px 0;text-align:right;font-weight:600;color:#1a1a2e;">$${subtotal.toFixed(2)}</td></tr>
    ${(() => {
      const discount = resolveBillingDocumentDiscount(doc)
      if (!discount) return ''
      return `<tr><td style="padding:6px 0;color:#64748b;">${escapeHtml(formatDiscountLabel(discount))}</td><td style="padding:6px 0;text-align:right;font-weight:600;color:#1a1a2e;">${escapeHtml(formatDiscountAmountDisplay(discount))}</td></tr>`
    })()}
    <tr><td style="padding:6px 0;color:#64748b;">Tax (${taxLabel})</td><td style="padding:6px 0;text-align:right;font-weight:600;color:#1a1a2e;font-size:${taxResolved ? '13px' : '11px'};">${escapeHtml(taxValue)}</td></tr>
    <tr><td style="padding:10px 0 0;border-top:2px solid #1a1a2e;font-size:15px;font-weight:800;color:#1a1a2e;">Total</td><td style="padding:10px 0 0;border-top:2px solid #1a1a2e;text-align:right;font-size:15px;font-weight:800;color:#1a1a2e;">${escapeHtml(totalValue)}</td></tr>`

  const qbNote =
    shouldIncludeEstimateQuickBooksPdfNote(doc)
      ? `<div style="margin-top:20px;padding:12px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;font-size:11px;line-height:1.55;color:#475569;">${escapeHtml(ESTIMATE_QUICKBOOKS_PDF_NOTE)}</div>`
      : ''

  return `
    <div style="box-sizing:border-box;width:720px;font-family:Helvetica,Arial,sans-serif;color:#1a1a2e;background:#ffffff;padding:28px;">
      <table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin-bottom:20px;">
        <tr>
          <td style="vertical-align:top;">
            <div style="font-size:10px;font-weight:800;letter-spacing:0.1em;text-transform:uppercase;color:#94a3b8;">RefReps</div>
            <h1 style="margin:6px 0 0;font-size:22px;font-weight:800;color:#1a1a2e;">${escapeHtml(typeLabel)}</h1>
            <div style="font-size:13px;color:#64748b;margin-top:4px;">#${escapeHtml(num)}</div>
          </td>
          <td style="vertical-align:top;text-align:right;">
            <span style="display:inline-block;font-size:10px;font-weight:700;padding:4px 10px;border-radius:999px;${statusMeta.emailBadgeStyle};">${escapeHtml(statusMeta.label)}</span>
          </td>
        </tr>
      </table>
      <table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin-bottom:20px;">
        <tr>${dateRows.map((cell) => `<td style="padding-right:20px;vertical-align:top;">${cell}</td>`).join('')}</tr>
      </table>
      <table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin-bottom:20px;">
        <tr>
          <td style="width:50%;padding-right:12px;vertical-align:top;">
            <div style="padding:14px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;">
              <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;margin-bottom:6px;">From</div>
              <div style="font-size:14px;font-weight:700;color:#1a1a2e;">${escapeHtml(REFREPS_BILLING_FROM.companyName)}</div>
              ${refRepsBillingFromAddressLines().map((line) => `<div style="font-size:12px;color:#64748b;margin-top:2px;">${escapeHtml(line)}</div>`).join('')}
              <div style="font-size:12px;color:#64748b;margin-top:4px;">${escapeHtml(REFREPS_BILLING_FROM.email)}</div>
              <div style="font-size:12px;color:#64748b;">${escapeHtml(REFREPS_BILLING_FROM.website)}</div>
            </div>
          </td>
          <td style="width:50%;padding-left:12px;vertical-align:top;">
            <div style="padding:14px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;">
              <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8;margin-bottom:6px;">Bill to</div>
              <div style="font-size:14px;font-weight:700;color:#1a1a2e;">${escapeHtml(billTo.orgName || '—')}</div>
              ${billTo.contactName ? `<div style="font-size:12px;color:#64748b;margin-top:2px;">${escapeHtml(billTo.contactName)}</div>` : ''}
              ${billTo.contactEmail ? `<div style="font-size:12px;color:#64748b;">${escapeHtml(billTo.contactEmail)}</div>` : ''}
              ${formatBillToAddress(billTo) ? `<div style="font-size:12px;color:#64748b;margin-top:4px;">${escapeHtml(formatBillToAddress(billTo))}</div>` : ''}
            </div>
          </td>
        </tr>
      </table>
      <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:16px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:8px 10px;text-align:left;font-size:10px;text-transform:uppercase;color:#64748b;">Item</th>
            <th style="padding:8px 10px;text-align:left;font-size:10px;text-transform:uppercase;color:#64748b;">Description</th>
            <th style="padding:8px 10px;text-align:right;font-size:10px;text-transform:uppercase;color:#64748b;">Qty</th>
            <th style="padding:8px 10px;text-align:right;font-size:10px;text-transform:uppercase;color:#64748b;">Unit</th>
            <th style="padding:8px 10px;text-align:right;font-size:10px;text-transform:uppercase;color:#64748b;">Amount</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <table role="presentation" cellpadding="0" cellspacing="0" style="width:240px;margin-left:auto;font-size:13px;">
        <tbody>${totalsRows}</tbody>
      </table>
      ${doc.notes ? `<div style="margin-top:20px;padding:12px 14px;background:#ffffff;border:1px dashed #cbd5e1;border-radius:8px;font-size:11px;color:#64748b;"><strong style="color:#1a1a2e;">Notes:</strong> ${escapeHtml(doc.notes)}</div>` : ''}
      ${qbNote}
    </div>
  `
}

/** Allow layout/paint before html2canvas snapshots the export node. */
function waitForPdfPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

/**
 * Generate and download a PDF for a billing document.
 * @param {Record<string, unknown>} doc
 * @param {string} [filename]
 * @param {{ orgTaxExempt?: boolean }} [options]
 */
export async function downloadBillingDocumentPdf(doc, filename, options = {}) {
  const mount = document.createElement('div')
  mount.setAttribute('aria-hidden', 'true')
  // Must stay in the viewport (not off-screen / behind the page) or html2canvas captures blank.
  mount.style.cssText =
    'position:fixed;left:0;top:0;width:720px;background:#ffffff;z-index:2147483646;pointer-events:none;overflow:visible;'
  mount.innerHTML = buildBillingDocumentPdfHtml(doc, options)
  document.body.appendChild(mount)

  const target = mount.firstElementChild || mount
  await waitForPdfPaint()

  const width = Math.ceil(target.scrollWidth || 720)
  const height = Math.ceil(target.scrollHeight || 400)
  void target.offsetHeight

  const num = String(doc.documentNumber || 'document').replace(/^#/, '')
  const typeSlug = billingDocumentListType(doc.documentType).replace(/[^\w]+/g, '-')
  const safeName = (filename || `${typeSlug}-${num}`).replace(/[^\w.-]+/g, '_')

  try {
    await html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `${safeName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          width,
          height,
          windowWidth: width,
          windowHeight: height,
          scrollX: 0,
          scrollY: -window.scrollY,
        },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      })
      .from(target)
      .save()
  } finally {
    document.body.removeChild(mount)
  }
}

/**
 * Download PDF for a purchase-history list row.
 * @param {{ entry: Record<string, unknown>, docs?: unknown[], billToFallback?: Record<string, unknown> }} opts
 */
export async function downloadBillingDocumentFromEntry({
  entry,
  docs = [],
  billToFallback = {},
}) {
  const doc = resolveBillingDocumentForDownload(docs, entry, billToFallback)
  if (!doc) {
    throw new Error('Unable to load this document for download')
  }
  if (!Array.isArray(doc.lineItems) || doc.lineItems.length === 0) {
    const amount = Number(entry?.amount) || Number(doc.total) || 0
    if (amount <= 0) {
      throw new Error('This document has no content to export')
    }
  }
  await downloadBillingDocumentPdf(doc)
}

/**
 * Resolve full billing document for a purchase-history row.
 * @param {unknown[]} docs
 * @param {Record<string, unknown> | null | undefined} entry
 */
export function findBillingDocumentForEntry(docs, entry) {
  if (!entry) return null
  if (entry.billingDocument && typeof entry.billingDocument === 'object') {
    return entry.billingDocument
  }
  const list = Array.isArray(docs) ? docs : []
  const docId = entry.documentId ?? entry.id
  const num = String(entry.number || '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase()
  return (
    list.find((d) => {
      const id = String(d.id ?? d._id ?? '')
      const docNum = String(d.documentNumber ?? '')
        .replace(/^#/, '')
        .trim()
        .toLowerCase()
      if (docId != null && docId !== '') {
        const want = String(docId).replace(/^#/, '').trim().toLowerCase()
        if (id && id === String(docId)) return true
        if (docNum && docNum === want) return true
      }
      if (num && docNum && docNum === num) return true
      return false
    }) ?? null
  )
}

/** @alias */
export const mapDocumentToPurchaseHistoryEntry = mapBillingDocumentToPurchaseHistoryEntry

/**
 * @param {unknown[]} docs
 */
export function mapBillingDocumentsToPurchaseHistory(docs) {
  return (Array.isArray(docs) ? docs : [])
    .filter((doc) =>
      ['quote', 'invoice', 'payment'].includes(String(doc?.documentType || '')) &&
      !isBillingDocumentHiddenFromPurchaseHistory(doc)
    )
    .map((doc) => mapBillingDocumentToPurchaseHistoryEntry(doc))
    .sort((a, b) => {
      const ta = new Date(a.purchaseDate).getTime()
      const tb = new Date(b.purchaseDate).getTime()
      return tb - ta
    })
}

/**
 * @param {string} companyId organization.companyID
 */
export async function fetchOrganizationQuickbooksCustomers(companyId) {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/get-organization-quickbooks-customers`,
      { companyId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to load billing contacts')
    }
    return Array.isArray(data.customers) ? data.customers : []
  } catch (err) {
    const parsed = parseBillingApiError(err)
    const e = new Error(parsed.message)
    e.status = parsed.status
    throw e
  }
}

/**
 * Unlink a QuickBooks customer from an organization's saved billing addresses.
 * @param {string} companyId organization.companyID
 * @param {string} customerId QuickBooks customer id
 */
export async function removeOrganizationQuickbooksCustomer(companyId, customerId) {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/remove-organization-quickbooks-customer`,
      { companyId, customerId },
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to remove billing address')
    }
    return Array.isArray(data.customers) ? data.customers : []
  } catch (err) {
    const parsed = parseBillingApiError(err)
    const e = new Error(parsed.message)
    e.status = parsed.status
    throw e
  }
}

/**
 * @param {string} organizationId organization.companyID
 */
export async function fetchOrganizationBillingDocuments(organizationId) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/get-organization-billing-documents`,
    { organizationId },
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return data?.organizationBillingDocuments ?? []
}

/**
 * Fetch the native QuickBooks PDF for an organization billing document.
 * @param {{ organizationId: string, documentId: string, disposition?: 'inline' | 'attachment' | 'download' }} opts
 * @returns {Promise<{ blob: Blob, fileName: string | null }>}
 */
export async function fetchOrganizationBillingDocumentPdf({
  organizationId,
  documentId,
  disposition = 'inline',
}) {
  const orgId = String(organizationId || '').trim()
  const docId = String(documentId || '').trim()
  if (!orgId) throw new Error('organizationId is required')
  if (!docId) throw new Error('documentId is required')

  const res = await fetch(
    `${import.meta.env.VITE_BASE_LINK}/get-organization-billing-document-pdf`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organizationId: orgId,
        documentId: docId,
        disposition,
      }),
    }
  )

  const contentType = res.headers.get('content-type') || ''
  if (!res.ok) {
    if (contentType.includes('application/json')) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data?.message || 'Failed to fetch PDF')
    }
    throw new Error('Failed to fetch PDF')
  }

  const blob = await res.blob()
  const dispositionHeader = res.headers.get('content-disposition') || ''
  const utfMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(dispositionHeader)
  const plainMatch = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(dispositionHeader)
  let fileName = null
  if (utfMatch?.[1]) {
    try {
      fileName = decodeURIComponent(utfMatch[1].trim())
    } catch {
      fileName = utfMatch[1].trim()
    }
  } else if (plainMatch) {
    fileName = String(plainMatch[1] || plainMatch[2] || '').trim().replace(/^"|"$/g, '') || null
  }

  return { blob, fileName }
}
